import { useState, useCallback } from 'react';
import { ViewerShell } from './ViewerShell';
import type { FavoriteCachedData } from '@/types/favorites';

interface ImageItem {
  url: string;
  title?: string;
}

interface ImageGalleryProps {
  images: ImageItem[];
  initialIndex?: number;
  onClose: () => void;
  onDownload?: () => void;
  fileId?: string;
  favoriteCachedData?: FavoriteCachedData;
}

export function ImageGallery({
  images,
  initialIndex = 0,
  onClose,
  onDownload,
  fileId,
  favoriteCachedData,
}: ImageGalleryProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;

  const resetTransform = useCallback(() => {
    setScale(1);
    setRotation(0);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      const idx = page - 1;
      if (idx >= 0 && idx < images.length) {
        resetTransform();
        setCurrentIndex(idx);
      }
    },
    [images.length, resetTransform]
  );

  return (
    <ViewerShell
      title={currentImage.title || `Image ${currentIndex + 1}`}
      subtitle={hasMultiple ? `${currentIndex + 1} of ${images.length}` : undefined}
      type="image"
      fileId={fileId}
      favoriteCachedData={favoriteCachedData}
      onClose={onClose}
      onDownload={onDownload}
      currentPage={hasMultiple ? currentIndex + 1 : undefined}
      totalPages={hasMultiple ? images.length : undefined}
      onPageChange={hasMultiple ? handlePageChange : undefined}
      scale={scale}
      onScaleChange={setScale}
      onRotate={() => setRotation((r) => (r + 90) % 360)}
      swipeEnabled={scale <= 1}
      keyboardHint={
        hasMultiple
          ? 'Arrow keys to navigate • +/- to zoom • R to rotate • Esc to close'
          : '+/- to zoom • R to rotate • Esc to close'
      }
    >
      <div className="flex flex-col min-h-full">
        <div className="flex-1 flex items-center justify-center overflow-auto p-4 sm:p-8">
          <img
            src={currentImage.url}
            alt={currentImage.title || `Image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain transition-transform duration-200 select-none"
            style={{ transform: `scale(${scale}) rotate(${rotation}deg)` }}
            draggable={false}
          />
        </div>

        {/* Thumbnail strip for multi-image */}
        {hasMultiple && (
          <div className="bg-white/80 backdrop-blur-sm p-2 border-t border-[#f0d2f0]/30 shrink-0">
            <div className="flex items-center justify-center gap-1.5 overflow-x-auto">
              {images.map((img, index) => (
                <button
                  key={img.url || `gallery-thumb-${index}`}
                  onClick={() => {
                    resetTransform();
                    setCurrentIndex(index);
                  }}
                  className={`shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    index === currentIndex
                      ? 'border-[#8e4585] scale-105'
                      : 'border-transparent opacity-50 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.url}
                    alt={img.title || `Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </ViewerShell>
  );
}
